import { BlockListContainer, FoodsIcon, FoodsSelect, HelperBox, Typography } from "@foodpilot/foods";
import { Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import {
    ActionPlanActionWrite,
    useUpsertActionPlanAction,
} from "src/api/client-api/ActionPlan/ActionPlanAction/useUpsertActionPlanAction";
import { useActionsContext } from "src/context/Actions/useActionsContext";

type TimerangeProps = {
    actionPlanAction: ActionPlanAction;
};
export const Timerange = (props: TimerangeProps) => {
    const { actionPlanAction } = props;
    const theme = useTheme();
    const { t } = useTranslation();

    return (
        <BlockListContainer
            header={
                <Stack
                    flexDirection={"row"}
                    gap={theme.spacing(4)}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: theme.spacing(3, 4, 3, 4),
                    }}
                >
                    <Typography variant="h4">{t("actionPlan.actionForm.dates.title")}</Typography>
                </Stack>
            }
            blocks={[<ActionDateSelect key={"action-date-select-timerange"} actionPlanAction={actionPlanAction} />]}
        />
    );
};

const formatToWrite = (value: ActionPlanAction): ActionPlanActionWrite => {
    const makeIRI = (value: number) => {
        return `/api/ssq_posts/${value}`;
    };

    return {
        id: value.id,
        actionId: value.actionId,
        actionPlan: value.actionPlan,
        startSimulationSsqPost: makeIRI(value.startSimulationSsqPost.id),
        endSimulationSsqPost: makeIRI(value.endSimulationSsqPost.id),
        type: " ",
    };
};

type ActionDateSelectProps = {
    actionPlanAction: ActionPlanAction;
};
const ActionDateSelect = (props: ActionDateSelectProps) => {
    const { actionPlanAction } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { actionPlanData, actionPlan, selectedScore, selectPostsByActionPlanAction, savingStatus } =
        useActionsContext();
    const upsertActionPlanAction = useUpsertActionPlanAction({ actionPlanID: actionPlan?.id });

    const _actionPlanData = actionPlanData.find(
        (ap) => ap.scoreId === selectedScore?.scoreId && ap.variationId === selectedScore.variationId,
    );
    if (_actionPlanData === undefined) return;

    const { start, target, posts } = selectPostsByActionPlanAction(_actionPlanData, actionPlanAction);

    return (
        <Stack marginBottom={theme.spacing(1)} gap={theme.spacing(2)}>
            <Stack flexDirection={"row"} justifyContent={"space-between"}>
                <Stack gap={theme.spacing(2)}>
                    <Typography variant={"h5"}>{t("actionPlan.actionForm.dates.start")}</Typography>
                    <FoodsSelect
                        selectedOption={start.post ?? null}
                        options={posts}
                        onChange={(selectedValue) => {
                            if (selectedValue === null) return;
                            const newActionPlanAction: ActionPlanAction = {
                                ...actionPlanAction,
                                startSimulationSsqPost: selectedValue,
                            };
                            const newData = formatToWrite(newActionPlanAction);
                            upsertActionPlanAction.mutate(newData, {
                                onSettled: () => {
                                    savingStatus.setSaving(false);
                                },
                            });
                        }}
                        disabled={savingStatus.isSaving}
                        getId={(item) => item.id}
                        getName={(item) => item.campaign?.label ?? "unknown label"}
                        textOptions={{
                            placeholder: t("component.select.placeholder"),
                            searchPlaceholder: t("component.select.searchPlaceholder"),
                            emptyOptionsText: t("component.select.noOptions"),
                            defaultValueText: t("component.select.defaultValue"),
                            noResult: t("component.select.noResultFound"),
                        }}
                    />
                </Stack>
                <Stack justifyContent={"end"}>
                    <FoodsIcon size={4} icon={"arrowRight"} />
                </Stack>
                <Stack gap={theme.spacing(2)}>
                    <Typography variant={"h5"}>{t("actionPlan.actionForm.dates.end")}</Typography>
                    <FoodsSelect
                        selectedOption={target.post ?? null}
                        options={posts}
                        onChange={(selectedValue) => {
                            if (selectedValue === null) return;
                            savingStatus.setSaving(true);
                            const newActionPlanAction: ActionPlanAction = {
                                ...actionPlanAction,
                                endSimulationSsqPost: selectedValue,
                            };
                            const newData = formatToWrite(newActionPlanAction);
                            upsertActionPlanAction.mutate(newData, {
                                onSettled: () => {
                                    savingStatus.setSaving(false);
                                },
                            });
                        }}
                        disabled={savingStatus.isSaving}
                        getId={(item) => item.id}
                        getName={(item) => item.campaign?.label ?? "unknown label"}
                        textOptions={{
                            placeholder: t("component.select.placeholder"),
                            searchPlaceholder: t("component.select.searchPlaceholder"),
                            emptyOptionsText: t("component.select.noOptions"),
                            defaultValueText: t("component.select.defaultValue"),
                            noResult: t("component.select.noResultFound"),
                        }}
                    />
                </Stack>
            </Stack>
            <HelperBox>
                <Typography variant="body">{t("actionPlan.actionForm.dates.info")}</Typography>
            </HelperBox>
        </Stack>
    );
};
